// ─── Cascade Builder Types ─────────────────────────────────────────────

export type NodeType =
  | 'customer-input'
  | 'capture-method'
  | 'validation'
  | 'aml-screening'
  | 'fraud-check'
  | 'risk-check'
  | 'decision'
  | 'fallback'

export type CaptureOwner = 'client' | 'vendor'

export type CaptureMethodType = 'form' | 'id-verification' | 'selfie' | 'data'

export type DecisionOutcome = 'pass' | 'fail' | 'partial-match' | 'no-match' | 'review'

export type FuzzinessLevel = 'strict' | 'moderate' | 'fuzzy' | 'optional'

export type CaptureMethod = 'user-input' | 'ocr' | 'existing-record' | 'api-lookup' | 'enrichment'

export type ValidationRuleset = '1+1' | '2+2' | '2-1-1' | '2-1-1-govid' | 'custom'

export interface DataField {
  id: string
  label: string
  enabled: boolean
  fuzziness: FuzzinessLevel
  captureMethod: CaptureMethod
}

export type DobFormat = 'full' | 'year-only'
export type AddressMethod = 'autofill' | 'broken-down' | 'open-field'
export type NationalIdType = 'passport' | 'drivers-licence' | 'other'
export type TaxIdType = 'ssn' | 'other'

/** Per-node collected field — which fields does THIS capture method collect? */
export interface CollectedField {
  id: string
  label: string
  enabled: boolean
  optional: boolean           // true = optional, false = required
  fuzziness: FuzzinessLevel   // kept for validation verified fields
  dobFormat?: DobFormat       // only for DOB field
  addressMethod?: AddressMethod // only for address field
  possessionVerified?: boolean  // only for email/phone — OTP/link sent to verify possession
  nationalIdType?: NationalIdType  // only for national_id field
  nationalIdOther?: string         // description when nationalIdType === 'other'
  taxIdType?: TaxIdType            // only for tax_id field
  taxIdOther?: string              // description when taxIdType === 'other'
  isCustom?: boolean               // true for user-added custom fields
}

export interface CascadeNode {
  id: string
  type: NodeType
  label: string
  sublabel?: string
  vendor?: string
  vendorId?: string
  description?: string
  verifies?: string[]
  requiresConsent?: boolean
  price?: string
  x: number
  y: number
  // Capture-method-specific fields
  captureMethodType?: CaptureMethodType
  captureOwner?: CaptureOwner        // client-side or vendor-hosted capture
  captureVendorName?: string          // vendor name when captureOwner === 'vendor'
  collectedFields?: CollectedField[]
  nodeNotes?: string
  // Validation-specific fields
  validationVendor?: string      // free-text vendor name
  validationRuleset?: ValidationRuleset | string
  verifiedFields?: CollectedField[]  // which fields this validation checks
  isSecondaryVendor?: boolean    // true = additional vendor in cascade (simpler display)
}

export interface CascadeEdge {
  id: string
  from: string
  to: string
  label?: string
  condition?: DecisionOutcome
}

export interface CascadeConfig {
  id: string
  country: string
  countryCode: string
  ruleset: string
  ruleDescription: string
  dataFields: DataField[]
  nodes: CascadeNode[]
  edges: CascadeEdge[]
  notes: string
  createdAt: string
  updatedAt: string
}

// ─── Choice system ─────────────────────────────────────────────────────

export interface ChoiceOption {
  id: string
  label: string
  sublabel?: string
  icon: string
  nodeType: NodeType
  captureMethodType?: CaptureMethodType
  vendor?: string
  vendorId?: string
  description?: string
  verifies?: string[]
  requiresConsent?: boolean
  price?: string
  children?: ChoiceOption[]  // sub-choices — picking this replaces bubbles with children
}

export interface ActiveChoice {
  sourceNodeId: string
  options: ChoiceOption[]
  baseX: number
  baseY: number
}

// ─── Capture method config ─────────────────────────────────────────────

export const CAPTURE_METHOD_TYPE_CONFIG: Record<CaptureMethodType, { label: string; icon: string; color: string }> = {
  form:   { label: 'Form',   icon: '📝', color: 'indigo' },
  'id-verification': { label: 'ID Document', icon: '🪪', color: 'violet' },
  selfie: { label: 'Biometric', icon: '🤳', color: 'pink' },
  data:   { label: 'Data',   icon: '📡', color: 'cyan' },
}

// ─── Default collected fields for a new capture node ───────────────────

export function getDefaultCollectedFields(captureMethodType?: CaptureMethodType): CollectedField[] {
  const nameEnabled = captureMethodType === 'form'
  const textFields: CollectedField[] = [
    { id: 'first_name',  label: 'First Name',   enabled: nameEnabled, optional: false, fuzziness: 'moderate' },
    { id: 'middle_name', label: 'Middle Name',   enabled: nameEnabled, optional: true,  fuzziness: 'fuzzy' },
    { id: 'last_name',   label: 'Last Name',     enabled: nameEnabled, optional: false, fuzziness: 'moderate' },
    { id: 'dob',         label: 'Date of Birth', enabled: false, optional: true,  fuzziness: 'strict', dobFormat: 'full' },
    { id: 'address',     label: 'Address',       enabled: false, optional: true,  fuzziness: 'fuzzy',  addressMethod: 'autofill' },
    { id: 'email',       label: 'Email',         enabled: false, optional: true,  fuzziness: 'strict' },
    { id: 'phone',       label: 'Phone',         enabled: false, optional: true,  fuzziness: 'strict' },
    { id: 'nationality', label: 'Nationality',    enabled: false, optional: true,  fuzziness: 'strict' },
    { id: 'national_id', label: 'National ID',   enabled: false, optional: true,  fuzziness: 'strict' },
    { id: 'tax_id',      label: 'Tax ID',        enabled: false, optional: true,  fuzziness: 'strict', taxIdType: 'ssn' },
  ]
  const docFields: CollectedField[] = [
    { id: 'doc_front',   label: 'Document Front', enabled: false, optional: true, fuzziness: 'strict' },
    { id: 'doc_back',    label: 'Document Back',  enabled: false, optional: true, fuzziness: 'strict' },
  ]
  const selfieField: CollectedField[] = [
    { id: 'selfie',      label: 'Selfie',         enabled: false, optional: true, fuzziness: 'strict' },
  ]

  switch (captureMethodType) {
    case 'form':
      return textFields
    case 'id-verification':
      return [...docFields, ...textFields]
    case 'selfie':
      return [...selfieField, ...docFields]
    default:
      return [...textFields, ...docFields, ...selfieField]
  }
}

// ─── Default verified fields for a validation node ─────────────────────

export function getDefaultVerifiedFields(): CollectedField[] {
  return [
    { id: 'first_name',  label: 'First Name',   enabled: false, optional: false, fuzziness: 'moderate' },
    { id: 'last_name',   label: 'Last Name',     enabled: false, optional: false, fuzziness: 'moderate' },
    { id: 'dob',         label: 'Date of Birth', enabled: false, optional: false, fuzziness: 'strict' },
    { id: 'address',     label: 'Address',       enabled: false, optional: false, fuzziness: 'fuzzy' },
    { id: 'national_id', label: 'National ID',   enabled: false, optional: false, fuzziness: 'strict' },
    { id: 'document',    label: 'Document',      enabled: false, optional: false, fuzziness: 'strict' },
    { id: 'selfie',      label: 'Selfie',        enabled: false, optional: false, fuzziness: 'strict' },
  ]
}

// ─── Colors & config ───────────────────────────────────────────────────

export const NODE_COLORS: Record<NodeType, { bg: string; border: string; text: string; icon: string }> = {
  'customer-input':   { bg: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-300 dark:border-slate-600', text: 'text-slate-700 dark:text-slate-200', icon: '👤' },
  'capture-method':   { bg: 'bg-violet-50 dark:bg-violet-900/30', border: 'border-violet-400 dark:border-violet-500', text: 'text-violet-700 dark:text-violet-300', icon: '📝' },
  'validation':       { bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-400 dark:border-blue-500', text: 'text-blue-700 dark:text-blue-300', icon: '🏛️' },
  'aml-screening':    { bg: 'bg-amber-50 dark:bg-amber-900/30', border: 'border-amber-400 dark:border-amber-500', text: 'text-amber-700 dark:text-amber-300', icon: '🔍' },
  'fraud-check':      { bg: 'bg-red-50 dark:bg-red-900/30', border: 'border-red-400 dark:border-red-500', text: 'text-red-700 dark:text-red-300', icon: '🚨' },
  'risk-check':       { bg: 'bg-purple-50 dark:bg-purple-900/30', border: 'border-purple-400 dark:border-purple-500', text: 'text-purple-700 dark:text-purple-300', icon: '📊' },
  'decision':         { bg: 'bg-green-50 dark:bg-green-900/30', border: 'border-green-400 dark:border-green-500', text: 'text-green-700 dark:text-green-300', icon: '✅' },
  'fallback':         { bg: 'bg-orange-50 dark:bg-orange-900/30', border: 'border-orange-400 dark:border-orange-500', text: 'text-orange-700 dark:text-orange-300', icon: '🔄' },
}

export const DECISION_COLORS: Record<DecisionOutcome, { bg: string; text: string; label: string }> = {
  'pass':          { bg: 'bg-emerald-500', text: 'text-white', label: 'Pass' },
  'fail':          { bg: 'bg-red-500', text: 'text-white', label: 'Fail' },
  'partial-match': { bg: 'bg-orange-500', text: 'text-white', label: 'Partial Match' },
  'no-match':      { bg: 'bg-red-400', text: 'text-white', label: 'No Match' },
  'review':        { bg: 'bg-amber-500', text: 'text-white', label: 'Manual Review' },
}

export const FUZZINESS_CONFIG: Record<FuzzinessLevel, { label: string; short: string; color: string; bg: string }> = {
  strict:   { label: 'Strict', short: 'S', color: 'text-red-400', bg: 'bg-red-500/15' },
  moderate: { label: 'Moderate', short: 'M', color: 'text-amber-400', bg: 'bg-amber-500/15' },
  fuzzy:    { label: 'Fuzzy', short: 'F', color: 'text-green-400', bg: 'bg-green-500/15' },
  optional: { label: 'Optional', short: 'O', color: 'text-gray-400', bg: 'bg-gray-500/15' },
}

export const CAPTURE_METHOD_CONFIG: Record<CaptureMethod, { label: string; icon: string }> = {
  'user-input':      { label: 'User Input', icon: '⌨️' },
  'ocr':             { label: 'OCR / Doc Scan', icon: '📷' },
  'existing-record': { label: 'Existing Record', icon: '📁' },
  'api-lookup':      { label: 'API Lookup', icon: '🔗' },
  'enrichment':      { label: 'Enrichment', icon: '✨' },
}

export const VALIDATION_RULESET_CONFIG: Record<ValidationRuleset, { label: string; description: string }> = {
  '1+1':         { label: '1+1', description: '1 name match, 1 additional (DOB or Address)' },
  '2+2':         { label: '2+2', description: '2 name matches, 2 additional (DOB + Address)' },
  '2-1-1':       { label: '2-1-1', description: '2 name, 1 DOB, 1 address from separate sources' },
  '2-1-1-govid': { label: '2-1-1 + Gov ID', description: '2-1-1 plus government ID verification' },
  'custom':      { label: 'Custom', description: 'Custom validation ruleset' },
}
